import { Button } from "@/components/ui/baseUi/button"
import { ErrorView } from "@/components/ui/layout/error/errorView"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import {
  TransactionAmountCell,
  TransactionDateCell,
  TransactionFeeCell,
  TransactionPaymentMethodCell,
  TransactionProductCell,
  TransactionStatusCell,
  TransactionUserCell,
} from "@/modules/finances/components/transactionTableComponents"
import type { TransactionEntity } from "@/modules/finances/entities/transactionEntity"
import { useFinanceSidebar } from "@/modules/finances/hooks/useFinanceSidebar"
import { PaymentsToValidateEnumApi } from "@/modules/finances/paymentsToValidate/gateway/paymentsToValidateApi"
import { usePaymentsToValidate } from "@/modules/finances/paymentsToValidate/hooks/usePaymentsToValidate"
import { usePaymentsToValidateActions } from "@/modules/finances/paymentsToValidate/hooks/usePaymentsToValidateActions"
import { BoxTransactionsDetailsLayout } from "@/modules/finances/transactions/components/boxTransactionDetailsLayout"
import { UserTransactionsDetailsLayout } from "@/modules/finances/transactions/components/userTransactionsDetailsLayout"
import { PaymentsToValidateColumnToSort } from "@/modules/finances/utils/financeUtils"
import { cn } from "@/modules/lib/utils"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { ColumnSortingOrder } from "@/modules/types/datatableTypes"
import { HUDatatable } from "@/modules/ui/components/dataTables/huDatatable"
import { HeaderTemplate } from "@/modules/ui/components/dataTables/templates/headerTemplate"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { useConfirmPopup } from "@/modules/ui/components/popup/huConfirmPopup"
import TransactionPendingEmptyDark from "@images/empty/pending_transaction_empty_dark.svg"
import { word } from "@modules/core/utils/i18n"
import { createColumnHelper } from "@tanstack/react-table"
import { CircleQuestionMark, XIcon } from "lucide-react"
import { Suspense, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"

type PaymentsToValidateTableProps = {
  paymentsToValidateEnumApi: PaymentsToValidateEnumApi
  className?: string
} & React.ComponentProps<"div">

const columnHelper = createColumnHelper<TransactionEntity>()

export function PaymentsToValidateTable({
  paymentsToValidateEnumApi,
  className,
  ...props
}: PaymentsToValidateTableProps) {
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionEntity>()

  const { sidebarNavigateTo } = useSidebar()
  const { navigateToFinanceFeesLayout } = useFinanceSidebar()
  const { abandonPaymentMutation } = usePaymentsToValidateActions()
  const confirmPopup = useConfirmPopup()
  const {
    data: paymentsToValidate,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage,
    hasNextPage,
    pageSize,
    setPageSize,
    totalElements,
    setSorting,
    sortBy,
    sortOrder,
  } = usePaymentsToValidate(paymentsToValidateEnumApi)

  const onSelectedTransaction = async (e: TransactionEntity) => {
    if (paymentsToValidateEnumApi === PaymentsToValidateEnumApi.OWNER) {
      await sidebarNavigateTo({
        content: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <BoxTransactionsDetailsLayout transactionId={e.transactionId} />
            </Suspense>
          </ErrorBoundary>
        ),
        onHide: () => {
          setSelectedTransaction(undefined)
          return Promise.resolve(true)
        },
        replaceAllComponents: true,
      }).then(() => {
        setSelectedTransaction(e)
      })
    } else {
      await sidebarNavigateTo({
        content: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <UserTransactionsDetailsLayout transactionId={e.transactionId} />
            </Suspense>
          </ErrorBoundary>
        ),
        onHide: () => {
          setSelectedTransaction(undefined)
          return Promise.resolve(true)
        },
        replaceAllComponents: true,
      }).then(() => {
        setSelectedTransaction(e)
      })
    }
  }

  const onAbandonTransaction = (event: React.MouseEvent<HTMLButtonElement>, transaction: TransactionEntity) => {
    event.preventDefault()
    event.stopPropagation()

    confirmPopup.show({
      message: word("finances.page.paymentToValidate.abandon.confirm.text"),
      footerProps: { confirmText: word("global.confirm.button") },
      accept: async () => {
        await abandonPaymentMutation.mutateAsync({
          api: paymentsToValidateEnumApi,
          transactionId: transaction.transactionId,
        })
      },
    })
  }

  const columns = [
    columnHelper.accessor("user", {
      header: () => (
        <HeaderTemplate
          title={word("finances.page.table.header.name")}
          variant={
            sortBy === PaymentsToValidateColumnToSort.USER
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(PaymentsToValidateColumnToSort.USER, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(PaymentsToValidateColumnToSort.USER, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: transaction } }) => <TransactionUserCell transaction={transaction} />,
    }),
    columnHelper.accessor("date", {
      header: () => (
        <HeaderTemplate
          title={word("finances.page.table.header.date")}
          variant={
            sortBy === PaymentsToValidateColumnToSort.DATE
              ? sortOrder === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(PaymentsToValidateColumnToSort.DATE, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(PaymentsToValidateColumnToSort.DATE, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: transaction } }) => <TransactionDateCell transaction={transaction} />,
    }),
    columnHelper.accessor("status", {
      header: () => (
        <HeaderTemplate
          title={word("finances.page.table.header.status")}
          variant={
            sortBy === PaymentsToValidateColumnToSort.STATUS
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(PaymentsToValidateColumnToSort.STATUS, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(PaymentsToValidateColumnToSort.STATUS, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: transaction } }) => <TransactionStatusCell transaction={transaction} />,
    }),
    columnHelper.accessor("product", {
      header: () => (
        <HeaderTemplate
          title={word("finances.page.table.header.offer")}
          variant={
            sortBy === PaymentsToValidateColumnToSort.PRODUCT
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(PaymentsToValidateColumnToSort.PRODUCT, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(PaymentsToValidateColumnToSort.PRODUCT, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: transaction } }) => <TransactionProductCell transaction={transaction} />,
    }),
    columnHelper.accessor("amount", {
      header: () => (
        <HeaderTemplate
          title={word("finances.page.table.header.price")}
          variant={
            sortBy === PaymentsToValidateColumnToSort.AMOUNT
              ? sortOrder === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(PaymentsToValidateColumnToSort.AMOUNT, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(PaymentsToValidateColumnToSort.AMOUNT, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: transaction } }) => <TransactionAmountCell transaction={transaction} />,
    }),
    columnHelper.accessor("fee", {
      header: () => (
        <Text
          className="flex cursor-pointer flex-row items-center"
          fontStyle="LL"
          onClick={() => navigateToFinanceFeesLayout(false)}
        >
          {word("finances.page.table.header.fee")}
          <CircleQuestionMark className="ml-2 size-3.5 text-textDefault-caption" />
        </Text>
      ),
      cell: ({ row: { original: transaction } }) => <TransactionFeeCell transaction={transaction} />,
    }),
    columnHelper.accessor("paymentMethodType", {
      header: () => (
        <HeaderTemplate
          title={word("finances.page.table.header.paymentMethod")}
          variant={
            sortBy === PaymentsToValidateColumnToSort.PAYMENT_METHOD
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(PaymentsToValidateColumnToSort.PAYMENT_METHOD, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(PaymentsToValidateColumnToSort.PAYMENT_METHOD, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: transaction } }) => <TransactionPaymentMethodCell transaction={transaction} />,
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: ({ row: { original: transaction } }) => {
        return (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghostNegative"
                  size="iconMd"
                  onClick={(event) => void onAbandonTransaction(event, transaction)}
                >
                  <XIcon className="size-4" color="currentColor" />
                </Button>
              }
            />
            <TooltipContent side="bottom" align="center">
              <Text fontStyle="BS">{word("finances.page.paymentToValidate.abandon.tooltipText")}</Text>
            </TooltipContent>
          </Tooltip>
        )
      },
      meta: {
        className: "cursor-pointer text-end!",
      },
    }),
  ]

  return (
    <div className={cn("flex w-full grow flex-col px-4", className)} {...props}>
      {paymentsToValidate.length <= 0 ? (
        <PaymentToValidateEmptyView />
      ) : (
        <HUDatatable
          data={paymentsToValidate}
          columns={columns}
          handleRowSelectionChange={(row: TransactionEntity) => void onSelectedTransaction(row)}
          selectedRowIndex={paymentsToValidate.findIndex(
            (item) => item.transactionId === selectedTransaction?.transactionId,
          )}
          pageSize={pageSize}
          setPageSize={setPageSize}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          totalElements={totalElements}
          className="*:table-fixed"
        />
      )}
    </div>
  )
}

function PaymentToValidateEmptyView() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-4xl bg-surface-background-secondary px-4 py-6">
      <img src={TransactionPendingEmptyDark} alt="Payment to validate Empty Data" className="max-w-[60%]" />
      <Text fontStyle="HS">{word("finances.page.paymentToValidate.empty.title")}</Text>
    </div>
  )
}
